/**
 * Clearing the local session metadata when a hard navigation is about to
 * follow.
 *
 * This file exists separately from the auth slice so the middleware, the app
 * bootstrap and the sign-out mutation can share one implementation without
 * importing each other.
 */

import { PERSIST_KEY } from '@bbkAdminRedux/persist-config';
import { clearTokensAction } from '@bbkAdminRedux/rtkq/auth-persisted-actions';

const AUTH_PERSISTED_SLICE = 'authPersisted-rtk';

/**
 * Remove the persisted session metadata from storage immediately.
 *
 * redux-persist keeps each whitelisted slice under `persist:<key>` as its own
 * JSON string, so the entry can be emptied in place without disturbing the
 * other slices. Storage can be unavailable or hold something unparseable, and
 * neither is worth blocking a sign-out over, so failures are ignored.
 */
const purgePersistedAuth = (): void => {
  const storageKey = `persist:${PERSIST_KEY}`;
  try {
    const persisted = window.localStorage.getItem(storageKey);
    if (!persisted) return;
    const slices = JSON.parse(persisted) as Record<string, string>;
    if (!(AUTH_PERSISTED_SLICE in slices)) return;
    slices[AUTH_PERSISTED_SLICE] = JSON.stringify({});
    window.localStorage.setItem(storageKey, JSON.stringify(slices));
  } catch {
    // Ignore: the redirect matters more than the purge.
  }
};

/**
 * Drop the local, non-secret session metadata from both the store and storage.
 *
 * Dispatching alone is not enough ahead of a hard navigation: redux-persist
 * batches writes (`PERSIST_THROTTLE_MS`), so the page is normally torn down
 * before the cleared state reaches storage. The next load then rehydrates the
 * stale metadata, concludes the session went missing and bounces to sign-in,
 * which rehydrates it again — a redirect loop that never reaches the sign-in
 * form. Writing the purge through synchronously closes that window.
 */
type ClearTokensDispatch = (
  action: ReturnType<typeof clearTokensAction>
) => unknown;

export const clearSessionMetadata = (dispatch: ClearTokensDispatch) => {
  dispatch(clearTokensAction());
  purgePersistedAuth();
};
